const MS_PER_DAY = 24 * 60 * 60 * 1000

const makeUTCDate = (year: number, monthIndex: number, day: number) => {
  // Create a stable UTC date at 12:00 UTC to avoid DST/timezone edge cases
  return new Date(Date.UTC(year, monthIndex, day, 12, 0, 0, 0))
}

export const parseDateStringAsUTC = (dateString: string) => {
  if (typeof dateString !== 'string') {
    throw new Error('dateString must be in YYYY-MM-DD format')
  }
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s|T|$)/)
  if (!match) {
    throw new Error('dateString must be in YYYY-MM-DD format')
  }

  const year: string = match[1]!
  const month: string = match[2]!
  const day: string = match[3]!
  return makeUTCDate(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10))
}

export const toUTCDateOnly = (date: Date) => {
  return makeUTCDate(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

export const formatYMDUTC = (date: Date) => {
  const y = date.getUTCFullYear()
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export const formatYMDLocal = (date: Date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export const formatMDYDotsUTC = (date: Date) => {
  const m = String(date.getUTCMonth() + 1).padStart(2, '0')
  const d = String(date.getUTCDate()).padStart(2, '0')
  const y = date.getUTCFullYear()
  return `${m}.${d}.${y}`
}

/**
 * Dashboard header date range label (`MM.DD.YYYY - MM.DD.YYYY`).
 * Matches `selectedTimePeriodDates` display in DashboardClient.
 */
export const formatDashboardDateRangeLabel = (
  dateFrom: string | null,
  dateTo: string | null,
  fallback = 'Selected date range',
): string => {
  if (!dateFrom || !dateTo) {
    return fallback
  }
  try {
    return `${formatMDYDotsUTC(parseDateStringAsUTC(dateFrom))} - ${formatMDYDotsUTC(
      parseDateStringAsUTC(dateTo),
    )}`
  } catch {
    return fallback
  }
}

const startOfWeekUTC = (date: Date) => {
  const d = makeUTCDate(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  const day = d.getUTCDay() // 0 = Sunday
  return new Date(d.getTime() - day * MS_PER_DAY)
}

const endOfWeekUTC = (date: Date) => {
  const start = startOfWeekUTC(date)
  return new Date(start.getTime() + 6 * MS_PER_DAY)
}

const startOfMonthUTC = (date: Date) => makeUTCDate(date.getUTCFullYear(), date.getUTCMonth(), 1)
const endOfMonthUTC = (date: Date) => makeUTCDate(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)

const startOfQuarterUTC = (date: Date) => {
  const month = date.getUTCMonth()
  const quarterStartMonth = Math.floor(month / 3) * 3
  return makeUTCDate(date.getUTCFullYear(), quarterStartMonth, 1)
}

const endOfQuarterUTC = (date: Date) => {
  const start = startOfQuarterUTC(date)
  // End is the last day of the third month in the quarter
  return makeUTCDate(start.getUTCFullYear(), start.getUTCMonth() + 3, 0)
}

// Subtract whole quarters in UTC (3 months per quarter)
const subQuartersUTC = (date: Date, n = 1) => {
  const d = toUTCDateOnly(date)
  const out = new Date(d.getTime())
  out.setUTCMonth(out.getUTCMonth() - 3 * n)
  return out
}

// Given 2025-01-01 12:00:00 and 2025-01-02 12:00:00, returns 2
export const diffDaysUTCInclusive = ({ start, end }: { start: Date; end: Date }) => {
  const s = toUTCDateOnly(start)
  const e = toUTCDateOnly(end)
  return Math.round((e.getTime() - s.getTime()) / MS_PER_DAY) + 1
}

export type DashboardGraphView = 'daily' | 'weekly' | 'monthly' | 'quarterly'

const GRAPH_VIEW_URL_PERIODS = {
  daily: 'days',
  weekly: 'weeks',
  monthly: 'months',
  quarterly: 'quarter',
} as const satisfies Record<DashboardGraphView, string>

const URL_PERIOD_TO_GRAPH_VIEW = Object.fromEntries(
  Object.entries(GRAPH_VIEW_URL_PERIODS).map(([view, period]) => [period, view]),
) as Record<string, DashboardGraphView>

export const graphViewToUrlPeriod = (view: string) => {
  if (view in GRAPH_VIEW_URL_PERIODS) {
    return GRAPH_VIEW_URL_PERIODS[view as DashboardGraphView]
  }
  return GRAPH_VIEW_URL_PERIODS.monthly
}

export const urlPeriodToGraphView = (period: string | null): DashboardGraphView | null => {
  if (!period) return null
  return URL_PERIOD_TO_GRAPH_VIEW[period] ?? null
}

export const pluralizeDays = (value: string): 'day' | 'days' => {
  const numValue = Number.parseFloat(value)
  return numValue === 1 ? 'day' : 'days'
}

export const enumPeriods = {
  LAST_7_DAYS: 'Last 7 Days',
  THIS_WEEK: 'This Week',
  LAST_WEEK: 'Last Week',
  THIS_MONTH: 'This Month',
  LAST_MONTH: 'Last Month',
  THIS_QUARTER: 'This Quarter',
  LAST_QUARTER: 'Last Quarter',
  CUSTOM_RANGE: 'Custom Range',
} as const

/*
 * timePeriodDates
 * used to highlight range days in the calendar
 */
export const timePeriodDates = (referenceDate = new Date()) => {
  const refUTC = toUTCDateOnly(referenceDate)

  const thisWeekStart = startOfWeekUTC(refUTC)
  const thisWeekEnd = endOfWeekUTC(refUTC)

  const lastWeekDate = new Date(refUTC.getTime() - 7 * MS_PER_DAY)
  const lastWeekStart = startOfWeekUTC(lastWeekDate)
  const lastWeekEnd = endOfWeekUTC(lastWeekDate)

  const thisMonthStart = startOfMonthUTC(refUTC)
  const thisMonthEnd = endOfMonthUTC(refUTC)

  const lastMonthDate = new Date(thisMonthStart.getTime() - MS_PER_DAY)
  const lastMonthStart = startOfMonthUTC(lastMonthDate)
  const lastMonthEnd = endOfMonthUTC(lastMonthDate)

  const thisQuarterStart = startOfQuarterUTC(refUTC)
  const thisQuarterEnd = endOfQuarterUTC(refUTC)

  const lastQuarterDate = subQuartersUTC(refUTC, 1)
  const lastQuarterStart = startOfQuarterUTC(lastQuarterDate)
  const lastQuarterEnd = endOfQuarterUTC(lastQuarterDate)

  return {
    [enumPeriods.LAST_7_DAYS]: {
      startDate: new Date(refUTC.getTime() - 7 * MS_PER_DAY),
      endDate: new Date(refUTC.getTime() - 1 * MS_PER_DAY),
    },
    [enumPeriods.THIS_WEEK]: {
      startDate: thisWeekStart,
      endDate: thisWeekEnd,
    },
    [enumPeriods.LAST_WEEK]: {
      startDate: lastWeekStart,
      endDate: lastWeekEnd,
    },
    [enumPeriods.THIS_MONTH]: {
      startDate: thisMonthStart,
      endDate: thisMonthEnd,
    },
    [enumPeriods.LAST_MONTH]: {
      startDate: lastMonthStart,
      endDate: lastMonthEnd,
    },
    [enumPeriods.THIS_QUARTER]: {
      startDate: thisQuarterStart,
      endDate: thisQuarterEnd,
    },
    [enumPeriods.LAST_QUARTER]: {
      startDate: lastQuarterStart,
      endDate: lastQuarterEnd,
    },
    [enumPeriods.CUSTOM_RANGE]: {
      startDate: refUTC,
      endDate: refUTC,
    },
  }
}

/* convertToSafariValidDate
 * @param "date" is always in the format "mm.dd.yyyy"
 * this is the business rule assumed by the Dashboard component where this value comes from
 * @return "a valid Safari date format"
 */
export const convertToSafariValidDate = (date: string) => {
  const dateArray = date.split('.')
  const year = Number(dateArray[2])
  const month = Number(dateArray[0])
  const dateOfMonth = Number(dateArray[1])
  return makeUTCDate(year, month - 1, dateOfMonth)
}
